import React from 'react';
import * as Yup from 'yup';
import { Col, Row } from 'antd';
import { Formik, FormikProps } from 'formik';
import { Form } from 'formik-antd';
import { isNil } from 'lodash';
import FormItem from 'components/formik/FormItem';
import InputNumber from 'components/formik/InputNumber';
import Checkbox from 'components/formik/Checkbox';
import FormikEffect from 'components/formik/FormikEffect';
import { AssignmentAdminAdditionalInfoModel } from 'api/adminApiClient';

type Props = {
  formRef: any;
  additionalInfo: AssignmentAdminAdditionalInfoModel;
  onChange: (formik: FormikProps<AdditionalInfoFormValues>) => void;
};

export interface AdditionalInfoFormValues {
  markup: number | null;
  grossMarginPercent: number | null;
  grossMarginPerHour: number | null;
  anticipatedWeeklyCommission: number | null;
  confirmationCommissionStructure: boolean;
}

const mapAdditionalAssignmentInfoToInitialValues = (
  additionalInfo: AssignmentAdminAdditionalInfoModel,
) => {
  return {
    markup: !isNil(additionalInfo?.markup) ? additionalInfo?.markup : null,
    grossMarginPercent: !isNil(additionalInfo?.grossMarginPercent)
      ? additionalInfo?.grossMarginPercent
      : null,
    grossMarginPerHour: !isNil(additionalInfo?.grossMarginPerHour)
      ? additionalInfo?.grossMarginPerHour
      : null,
    anticipatedWeeklyCommission: !isNil(
      additionalInfo?.anticipatedWeeklyCommission,
    )
      ? additionalInfo?.anticipatedWeeklyCommission
      : null,
    confirmationCommissionStructure:
      additionalInfo?.confirmationCommissionStructure || false,
  };
};

const validationSchema = Yup.object({
  markup: Yup.number()
    .nullable()
    .min(0, 'Markup should be from 0 to 999999')
    .max(999999, 'Markup should be from 0 to 999999'),
  grossMarginPercent: Yup.number()
    .nullable()
    .min(0, 'Gross margin % should be from 0 to 100')
    .max(100, 'Gross margin % should be from 0 to 100'),
  grossMarginPerHour: Yup.number()
    .nullable()
    .min(0, 'Gross margin/hr should be from 0 to 999999')
    .max(999999, 'Gross margin/hr should be from 0 to 999999'),
  anticipatedWeeklyCommission: Yup.number()
    .nullable()
    .min(0, 'Anticipated weekly commission should be from 0 to 999999')
    .max(999999, 'Anticipated weekly commission should be from 0 to 999999'),
  confirmationCommissionStructure: Yup.boolean(),
});

export default React.memo((props: Props) => {
  const { formRef, additionalInfo, onChange } = props;
  const initialValues =
    mapAdditionalAssignmentInfoToInitialValues(additionalInfo);

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={initialValues}
      onSubmit={() => {}}
      validationSchema={validationSchema}
    >
      <>
        <FormikEffect onChange={onChange} />

        <Form layout="vertical" className="form-xxl-fixed-size">
          <div className="modal-title">Additional information</div>
          <Row gutter={40}>
            <Col xs={24} md={12}>
              <FormItem label="Markup" name="markup">
                <InputNumber
                  name="markup"
                  placeholder="Enter markup"
                  precision={2}
                />
              </FormItem>
            </Col>

            <Col xs={24} md={12}>
              <FormItem label="Gross margin %" name="grossMarginPercent">
                <InputNumber
                  name="grossMarginPercent"
                  placeholder="Enter gross margin %"
                  precision={2}
                />
              </FormItem>
            </Col>

            <Col xs={24} md={12}>
              <FormItem label="Gross margin/hr" name="grossMarginPerHour">
                <InputNumber
                  name="grossMarginPerHour"
                  placeholder="Enter gross margin/hr"
                  precision={2}
                />
              </FormItem>
            </Col>

            <Col xs={24} md={12}>
              <FormItem
                label="Anticipated weekly commission"
                name="anticipatedWeeklyCommission"
              >
                <InputNumber
                  name="anticipatedWeeklyCommission"
                  placeholder="Enter weekly commission"
                  precision={2}
                />
              </FormItem>
            </Col>

            <Col xs={24} md={12}>
              <FormItem
                name="confirmationCommissionStructure"
                className="checkbox-wrapper"
              >
                <Checkbox name="confirmationCommissionStructure">
                  Confirm commission structure
                </Checkbox>
              </FormItem>
            </Col>
          </Row>
        </Form>
      </>
    </Formik>
  );
});
